import { Suspense, useEffect, useState } from 'react';

import { Constants } from '@constants';
import { UKOnly } from '@iwIntlProvider/CountryWrappers';
import { tracking } from '@iwoca/frontend-tracking-library';
import { Button } from '@iwoca/orion';
import { REQUEST_CHANGE_ROUTER } from '@iwRouter/IwRouter.actions';
import { Text } from '@ui';
import { defineMessage, FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import { useGetDrawdownActionPanelData } from './useGetDrawdownActionPanelData';
import { ErrorBoundary } from '../../../../errorBoundary/ErrorBoundary';
import { isMobileWidth } from '../../../../iwMedia/BreakpointHelper';
import { GET_REPAYMENT_SCHEDULE } from '../../../drawDown/DrawDown.actions';
import { buildLocationWithSearch } from '../../../drawDown/DrawDownHelper';
import FallbackErrorModal from '../../../modals/fallbackErrorModal/FallbackErrorModal';
import MainContentPending from '../../../pageStructure/mainContentPending';
import { ChooseLoanAmount } from '../../../sharedDashboardComponents/slider/chooseLoanAmount/ChooseLoanAmount';
import { MoneyNoDecimals } from '../../../ui/moneyNoDecimals/MoneyNoDecimals';
import RepaymentScheduleSummary from '../../dashboardComponents/repaymentScheduleSummary/RepaymentScheduleSummary';

const interestOnlyWithdrawMessage = defineMessage({
    id: '_.outstanding_view.dashboardActionPanel.interestOnlyWithdrawMessage',
    defaultMessage:
        'I want to extend my interest-only period and withdraw {input}'
});

const withdrawMessage = defineMessage({
    id: '_.outstanding_view.dashboardActionPanel.withdrawMessage',
    defaultMessage: 'I want to withdraw {input}'
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    continueToDrawdown: (amount: number, offerId: string) => {
        dispatch({
            type: REQUEST_CHANGE_ROUTER,
            //@ts-ignore
            location: buildLocationWithSearch({
                pathname: '/funding-requirements/',
                offerId: offerId,
                amount: amount
            })
        });
    },
    getRepaymentSchedule: (
        amount: number,
        offerId: string,
        productType: string
    ) => {
        dispatch({
            type: GET_REPAYMENT_SCHEDULE,
            amount,
            offerId,
            productType
        });
    }
});

type PropsFromRedux = ReturnType<typeof mapDispatchToProps>;

export const TopUpDrawDown = connect(
    null,
    mapDispatchToProps
)(({
    getRepaymentSchedule,
    continueToDrawdown,
    accountId
}: { accountId: string } & PropsFromRedux) => {
    const {
        topUpStatus,
        availableCash,
        drawdownOffer,
        isInInterestOnlyRepaymentPeriod
    } = useGetDrawdownActionPanelData(accountId);

    const [topupDrawdownAmount, setTopupDrawdownAmount] = useState(
        availableCash ? Math.floor(availableCash) : 0
    );

    useEffect(() => {
        if (drawdownOffer) {
            getRepaymentSchedule(
                topupDrawdownAmount,
                drawdownOffer.offer_id!,
                drawdownOffer.product_type!
            );
        }
    }, [getRepaymentSchedule, drawdownOffer, topupDrawdownAmount]);

    if (!availableCash) {
        return null;
    }
    return (
        <div className="flex flex-col gap-xl">
            <UKOnly>
                {isInInterestOnlyRepaymentPeriod &&
                    topUpStatus === 'TOPUP_APPROVED' && (
                        <Text>
                            <FormattedMessage
                                id="_.drawdown_action_panel.topup_drawdown.interest_only_disclaimer"
                                defaultMessage="Just so you know, when you top up your interest-only period will end. You can see what your payments would look like below"
                            />
                        </Text>
                    )}
            </UKOnly>

            <ChooseLoanAmount
                drawdownAmount={Math.floor(topupDrawdownAmount)}
                minimumDrawdownAmount={Constants.MIN_DRAW_DOWN_AMOUNT}
                maximumDrawdownAmount={availableCash}
                updateDrawdownAmount={setTopupDrawdownAmount}
                data-testid="drawdown-amount-input"
                label={
                    topUpStatus ===
                    'INTEREST_ONLY_EXTENSION_APPROVED_WITH_MORE_FUNDS'
                        ? interestOnlyWithdrawMessage
                        : withdrawMessage
                }
                showSlider={!isMobileWidth()}
            />

            <Button
                data-testid="continue-to-drawdown-button"
                className="w-full"
                variant="success"
                onClick={() => {
                    tracking.ampli.drawdownCtaClicked();
                    continueToDrawdown(
                        topupDrawdownAmount,
                        drawdownOffer?.offer_id!
                    );
                }}
                disabled={
                    topupDrawdownAmount < Constants.MIN_DRAW_DOWN_AMOUNT ||
                    topupDrawdownAmount > availableCash
                }
            >
                <FormattedMessage
                    id="_.outstanding_view.dashboardActionPanel.drawdownAmount"
                    defaultMessage="Continue with {drawdownAmount}"
                    values={{
                        drawdownAmount: (
                            <MoneyNoDecimals
                                value={Math.floor(topupDrawdownAmount)}
                            />
                        )
                    }}
                />
            </Button>
            <ErrorBoundary fallback={() => <FallbackErrorModal />}>
                <Suspense
                    fallback={
                        <div
                            className="flex-1 rounded-m bg-structure-100 p-l shadow-black-s"
                            data-testId="loan-overview-loading"
                        >
                            <MainContentPending />
                        </div>
                    }
                >
                    <RepaymentScheduleSummary
                        topupDrawdownAmount={topupDrawdownAmount}
                        accountId={accountId}
                    />
                </Suspense>
            </ErrorBoundary>
        </div>
    );
});
